import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccessTime,
  AccountCircle,
  Campaign,
  Cancel,
  CheckCircleOutline,
  Close,
  Delete,
  Edit,
  Lightbulb,
  MoreVert,
  NavigateBefore,
  NavigateNext,
  Visibility,
} from "@mui/icons-material";
import { PreviewTemplate } from "../Preview/PreviewTemplate";
import { LoadState } from "../../constants/enums";
import { formatText } from "../../helpers";
import WhatsappBgImg from "../../assets/whatsapp_bg_img.jpg";
import { useMarketCampaignContext } from "../../views/market-campaign/MarketCampaign.context-state";
import { StatusRenderer } from "../ActiveInActiveStatus/ActiveInActiveStatus";
import { IWhatsAppTemplateData } from "../../redux/whatsappTemplate/whatsappTemplate.types";
import { useDispatchWrapper } from "../../hooks";
import { deleteQuickReplyTemplate } from "../../redux/quickReplyTemplates/quickReplyTemplateAction";
import { deleteWhatsAppTemplate } from "../../redux/whatsappTemplate/whatsappTemplateAction";
import { useNavigate } from "react-router-dom";

export const statusIconMap = {
  approved: <CheckCircleOutline sx={{ color: "#047203", fontSize: 28 }} />,
  pending: <AccessTime sx={{ color: "#ffa913", fontSize: 28 }} />,
  rejected: <Cancel sx={{ color: "#E53935", fontSize: 28 }} />,
};

export const categoryIconMap = {
  authentication: <AccountCircle sx={{ color: "#047203", fontSize: 28 }} />,
  marketing: <Campaign sx={{ color: "#136eff", fontSize: 28 }} />,
  utility: <Lightbulb sx={{ color: "#ffa913", fontSize: 28 }} />,
};

interface WhatsAppTemplateCardsProps {
  fetchList?: () => void;
  templates: IWhatsAppTemplateData[];
  selectedTemplate: string | null;
  onTemplateSelect: (
    name: string,
    company_outlet_uuid: string,
    template: IWhatsAppTemplateData,
  ) => (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  currentPage?: number;
  totalRecords?: number;
  onPageChange: (
    direction?: "next" | "prev",
    cursor?: string,
    rowsPerPage?: number,
  ) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPage: number;
  searchTemplateName?: string;
  onTemplatePreview: (template: IWhatsAppTemplateData) => void;
  onTemplateDetails: (template: IWhatsAppTemplateData) => void;
  showCheckbox?: boolean;
  isFromQuickReply?: boolean;
  isFromChatModule?: {
    toNumber?: string;
    isFromStartConversation?: boolean;
    isFromQuickReply?: boolean;
    onClose?: () => void;
  };
}

export const WhatsAppTemplateCardsSkeletonV2: React.FC<{
  count?: number;
}> = ({ count = 9 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box mt={2}>
      <Grid
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {Array.from({ length: count }).map((_, idx) => (
          <Grid item xs={12} sm={6} md={6} lg={4} key={idx} mb={4}>
            <Card
              sx={{
                height: "100%",
                minHeight: "400px",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                borderRadius: 2,
                boxShadow: 1,
                backgroundColor: "#efeae2",
                backgroundImage: `url(${WhatsappBgImg})`,
                backgroundRepeat: "repeat",
                backgroundSize: "auto",
                backgroundBlendMode: "multiply",
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Skeleton
                  variant="text"
                  width="70%"
                  height={32}
                  sx={{ mb: 1 }}
                />

                <Box
                  sx={{
                    height: "100%",
                    mb: 2,
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <Skeleton
                      variant="rounded"
                      height={isMobile ? 220 : 250}
                      sx={{ borderRadius: 2 }}
                    />
                    <Box sx={{ mt: 2 }}>
                      <Skeleton variant="text" width="85%" height={22} />
                      <Skeleton variant="text" width="75%" height={22} />
                      <Skeleton variant="text" width="60%" height={22} />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        sx={{ opacity: 0.7 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Templates per page:
          </Typography>
          <Skeleton variant="rounded" width={64} height={32} />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="rounded" width={96} height={32} />
          <Skeleton variant="rounded" width={96} height={32} />
        </Stack>
      </Box>
    </Box>
  );
};

export const WhatsAppTemplateCardsV2: React.FC<WhatsAppTemplateCardsProps> = ({
  fetchList,
  templates,
  selectedTemplate,
  onTemplateSelect,
  // paging,
  currentPage = 1,
  totalRecords = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPage,
  searchTemplateName,
  onTemplatePreview,
  onTemplateDetails,
  showCheckbox = true,
  isFromQuickReply = false,
  isFromChatModule,
}) => {
  const {
    marketCampaign: campaignBasicInfo,
    isEditable: isEditCampaignAllowed,
  } = useMarketCampaignContext();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const filteredTemplates = templates.filter((template) => {
    if (!searchTemplateName) return true;
    const searchTerms = searchTemplateName.toLowerCase().trim().split(/\s+/);
    const templateName = template.name.toLowerCase().trim();
    return searchTerms.every((term) => templateName.includes(term));
  });

  // New traditional pagination logic
  const isBackDisabled = currentPage <= 1;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const isNextDisabled =
    currentPage >= totalPages || templates.length < rowsPerPage;

  const [actionAnchorEl, setActionAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [selectedTemplateForAction, setSelectedTemplateForAction] =
    React.useState<IWhatsAppTemplateData | null>(null);

  const handleOpenActions = (
    event: React.MouseEvent<HTMLElement>,
    template: IWhatsAppTemplateData,
  ) => {
    event.stopPropagation();
    setActionAnchorEl(event.currentTarget);
    setSelectedTemplateForAction(template);
  };

  const handleCloseActions = () => {
    setActionAnchorEl(null);
    setSelectedTemplateForAction(null);
  };

  const handleDeleteTemplate = (template: IWhatsAppTemplateData) => {
    if (isFromQuickReply) {
      dispatch(
        deleteQuickReplyTemplate(
          template.company_outlet_uuid,
          template.name,
          (isSuccess) => {
            if (isSuccess) {
              fetchList?.();
            }
          },
        ),
      );
    } else {
      dispatch(
        deleteWhatsAppTemplate(
          template.company_outlet_uuid,
          template.name,
          (isSuccess) => {
            if (isSuccess) {
              fetchList?.();
            }
          },
        ),
      );
    }
  };

  const handleEditTemplate = (template: IWhatsAppTemplateData) => {
    navigate(
      `/meta-templates/manage/${(template as IWhatsAppTemplateData).meta_id}`,
    );
  };

  const open = Boolean(actionAnchorEl);

  return (
    <>
      <Box mt={2}>
        <Grid
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {filteredTemplates.map((template) => {
            const isApproved = isFromQuickReply
              ? template.status === "ACTIVE"
              : template.status === "APPROVED";
            const categoryIcon = template.category
              ? categoryIconMap[
                  template.category.toLowerCase() as keyof typeof categoryIconMap
                ]
              : null;
            const statusIcon = template.status
              ? statusIconMap[
                  template.status.toLowerCase() as keyof typeof statusIconMap
                ]
              : null;

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                lg={4}
                key={template.name}
                mb={4}
              >
                <Card
                  onClick={(e) => {
                    if (!isApproved) {
                      e.stopPropagation();
                      return;
                    }
                    if (
                      isEditCampaignAllowed ||
                      template.name === selectedTemplate
                    ) {
                      e.stopPropagation();

                      const mockEvent = {
                        target: {
                          checked: !(template.name === selectedTemplate),
                        },
                      } as unknown as React.ChangeEvent<HTMLInputElement>;
                      onTemplateSelect(
                        template.name,
                        template.company_outlet_uuid,
                        template,
                      )(mockEvent, !(template.name === selectedTemplate));
                      onTemplateDetails(template);
                    }
                  }}
                  sx={{
                    height: "100%",
                    minHeight: "400px",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    borderRadius: 2,
                    boxShadow: 1,
                    // opacity: isApproved ? 1 : 0.8,
                    cursor: isApproved ? "pointer" : "not-allowed",
                    backgroundColor: "#efeae2", // WhatsApp beige
                    backgroundImage: `url(${WhatsappBgImg})`,
                    backgroundRepeat: "repeat", // tile pattern
                    backgroundSize: "auto", // no stretch
                    backgroundBlendMode: "multiply", // ⭐ KEY FIX
                  }}
                >
                  {!isFromChatModule && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        position: "absolute",
                        top: { xs: 20, sm: 16 },
                        right: { xs: 0, sm: 0 },
                        zIndex: 1,
                      }}
                    >
                      {categoryIcon && (
                        <Tooltip title={template.category}>
                          {categoryIcon}
                        </Tooltip>
                      )}

                      {statusIcon && (
                        <Tooltip title={template.status}>
                          {statusIcon}
                        </Tooltip>
                      )}

                      <IconButton
                        onClick={(e) => handleOpenActions(e, template)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  )}

                  {showCheckbox && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 1,
                      }}
                    >
                      <Checkbox
                        checked={template.name === selectedTemplate}
                        onChange={onTemplateSelect(
                          template.name,
                          template.company_outlet_uuid,
                          template,
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isApproved) {
                            onTemplateDetails(template);
                          }
                        }}
                        disabled={!isApproved}
                        color="primary"
                      />
                    </Box>
                  )}

                  <CardContent
                    sx={{
                      flexGrow: 1,
                      cursor: isApproved
                        ? (campaignBasicInfo.status !== "DONE" &&
                            campaignBasicInfo.status !== "RUNNING") ||
                          template.name === selectedTemplate
                          ? "pointer"
                          : "not-allowed"
                        : "not-allowed",
                    }}
                    onClick={() => {
                      if (isApproved) {
                        onTemplatePreview(template);
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        mt: { xs: 0, sm: -1 },
                      }}
                    >
                      {formatText(template.name)}
                    </Typography>

                    <Box
                      sx={{
                        height: "100%",
                        mb: 2,
                        display: "flex",
                        alignItems: "flex-end",
                      }}
                    >
                      <PreviewTemplate
                        template={{
                          ...template,
                          header: template.header || {
                            type: "HEADER",
                            format: null,
                            text: null,
                            example: { header_text: [], header_handle: null },
                          },
                          body: template.body || {
                            type: "BODY",
                            text: "",
                            example: { body_text: [] },
                          },
                          footer: template.footer || {
                            type: "FOOTER",
                            text: "",
                          },
                          buttons: template.buttons || {
                            type: "BUTTONS",
                            buttons: [],
                          },
                        }}
                        loading={LoadState.InProgress}
                        headerType={template.header?.format || ""}
                        // compactMode={true}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={isMobile && isFromChatModule ? 1 : 2}
          sx={{
            ...(isMobile && isFromChatModule
              ? {
                  flexWrap: "wrap",
                  gap: 1,
                }
              : {}),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: isMobile && isFromChatModule ? "0.75rem" : undefined,
              }}
            >
              {isMobile && isFromChatModule
                ? "Rows per page:"
                : "Templates per page:"}
            </Typography>
            <FormControl size="small">
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  const newRowsPerPage = e.target.value as number;
                  onRowsPerPageChange(newRowsPerPage);
                  onPageChange(undefined, undefined, newRowsPerPage);
                }}
                sx={{
                  color: "text.secondary",
                  fontSize:
                    isMobile && isFromChatModule ? "0.75rem" : undefined,
                }}
              >
                <MenuItem value={9}>9</MenuItem>
                <MenuItem value={18}>18</MenuItem>
                <MenuItem value={36}>36</MenuItem>
                <MenuItem value={72}>72</MenuItem>
                <MenuItem value={144}>144</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction="row"
            spacing={isMobile && isFromChatModule ? 0.5 : 1}
            alignItems="center"
          >
            <IconButton
              onClick={() => onPageChange("prev")}
              disabled={isBackDisabled}
              size={isMobile && isFromChatModule ? "small" : "medium"}
              sx={{
                ...(isMobile && isFromChatModule
                  ? {
                      padding: "4px",
                    }
                  : {}),
              }}
            >
              <NavigateBefore />
              <Typography
                variant="body2"
                sx={{
                  fontSize:
                    isMobile && isFromChatModule ? "0.75rem" : undefined,
                }}
              >
                Back
              </Typography>
            </IconButton>

            <IconButton
              onClick={() => onPageChange("next")}
              disabled={isNextDisabled}
              size={isMobile && isFromChatModule ? "small" : "medium"}
              sx={{
                ...(isMobile && isFromChatModule
                  ? {
                      padding: "4px",
                    }
                  : {}),
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize:
                    isMobile && isFromChatModule ? "0.75rem" : undefined,
                }}
              >
                Next
              </Typography>
              <NavigateNext />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={actionAnchorEl}
        onClose={handleCloseActions}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0,0,0,0.5)" },
          },
        }}
      >
        {selectedTemplateForAction && (
          <Box p={2} minWidth={270}>
            <Stack spacing={1}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography variant="caption">Status: </Typography>
                <StatusRenderer
                  status={selectedTemplateForAction.status || ""}
                />
              </Stack>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography variant="caption">Category: </Typography>
                <StatusRenderer
                  status={selectedTemplateForAction.category || ""}
                />
              </Stack>

              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography variant="caption">Template For: </Typography>
                <StatusRenderer
                  status={
                    selectedTemplateForAction.template_for?.join(",") || "--"
                  }
                />
              </Stack>

              <Divider />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    handleCloseActions();
                  }}
                >
                  close
                </Button>
                <Box sx={{ display: "flex" }}>
                  <IconButton
                    onClick={() => {
                      handleDeleteTemplate(selectedTemplateForAction);
                    }}
                  >
                    <Delete color="error" />
                  </IconButton>
                  {selectedTemplateForAction.status === "REJECTED" && (
                    <IconButton
                      onClick={() => {
                        handleEditTemplate(selectedTemplateForAction);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Stack>
          </Box>
        )}
      </Popover>
    </>
  );
};
